import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { Outlet, Link, useLocation } from 'react-router-dom';

import { SideNav } from '../Navigation/SideNav';
import { LegacySideNav } from '../Navigation/LegacySideNav';
import { useProfileVersion } from '../Vendors/profileVersion';
import './styleOverrides.css';
import { Button, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { setAssessmentLocalStorageInit } from '../../app/store/assessmentSlice';
import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import { useNavigate } from 'react-router-dom';
import { resetAssessment } from '../../app/store/assessmentSlice';
import { resetSurvey } from '../../app/store/surveySlice';
import { resetQuestionnaireAnswer } from '../../app/store/questionnaireAnswerSlice';
import MasqueradeBanner from '../Masquerade/MasqueradeBanner';

interface LayoutProps {
  buttonIcon?: React.ElementType;
  buttonIconTwo?: React.ElementType;
  secondaryButtonClick?: () => void | undefined
  buttonTextTwo?: string;
  buttonText?: string;
  link?: string;
  linkTwo?: string;
  pageTitle: string;
  children: React.ReactNode;
  includeHeader: boolean;
  includeDivider: boolean;
  /** Edge-to-edge content with no page heading/padding/outer scroll (sub-nav shells manage their own scroll). */
  fullBleed: boolean;
}

function Layout({
  buttonIconTwo,
  buttonIcon,
  pageTitle,
  children,
  link,
  linkTwo,
  buttonText,
  buttonTextTwo,
  includeHeader,
  includeDivider,
  fullBleed,
  secondaryButtonClick,
}: LayoutProps) {
  const theme = useTheme();
  const { user } = useAppSelector(state => state.user);
  const firstPath = location.pathname.split('/')[1];
  const { assessmentLocalStorageItems } = useAppSelector(state => state.assessments);
  const dispatch = useAppDispatch();
  const routerLocation = useLocation();
  const [userInfoOpen, setUserInfoOpen] = useState(true);
  const handleUserInfoClose = () => setUserInfoOpen(false);
  const navigate = useNavigate();

  // The new header + 90px icon rail tracks the same v1/v2 state as the Company
  // Profile (shared via ProfileVersionProvider): the new shell renders only when
  // the user sees v2; everyone else (or before the flags resolve) gets the drawer.
  const { showV2: showNavV2, loaded: navFlagLoaded } = useProfileVersion();

  useEffect(() => {
    if (!assessmentLocalStorageItems.length) dispatch(setAssessmentLocalStorageInit());
  }, []);

  const isInQuestionnairePage = () => {
    if (routerLocation.pathname.includes('questionnaire')) {
      return true;
    }

    return false;
  };

  const handleNavigate = React.useCallback(() => {
    if (!linkTwo) return;
    dispatch(resetSurvey());
    dispatch(resetQuestionnaireAnswer());
    dispatch(resetAssessment());
    navigate(linkTwo);
    window.location.href = linkTwo;
  }, [dispatch, navigate, linkTwo]);

  const pageBody = (
    <>
      <MasqueradeBanner />
      {includeHeader && !fullBleed && (
   <Box
     sx={{
       display: 'flex',
       justifyContent: 'space-between',
       alignItems: 'center',
     }}
   >
     <Typography
       variant="h5"
       sx={{ fontWeight: '300', color: isInQuestionnairePage() ? theme.palette.customThemeColors?.darkGrey : 'currentcolor' }}
     >
          {isInQuestionnairePage()
            ? pageTitle
            : <Link to={'/' + firstPath} style={{
              textDecoration: 'none',
              fontWeight: '300',
              color: theme.palette.customThemeColors?.darkGrey,
            }}>{pageTitle}</Link>
          }
          </Typography>

          {link && (
          <Box
            sx={{
              ml: 'auto',
              display: 'flex',
              gap: 2,
            }}
          >
         <Link to={link}>
          <Button variant='outlined' size='small' startIcon={buttonIcon ? React.createElement(buttonIcon) : undefined}>
             {buttonText}
           </Button>
         </Link>
         {(linkTwo || !!secondaryButtonClick) && (
            <Button onClick={secondaryButtonClick ? secondaryButtonClick : handleNavigate} variant='contained' size='small' startIcon={buttonIconTwo ? React.createElement(buttonIconTwo) : undefined}>
              {buttonTextTwo}
            </Button>
         )}
       </Box>
          )}
   </Box>
      )}
        {includeDivider && !fullBleed && <Divider sx={{ mb: 1, pt: 1 }}/>}
        <Outlet />
        { children }
        {!user?.firstName && !user?.lastName && location.pathname.indexOf('user-profile') === -1 && <StandardDialog title='Please Finish Entering your User Information' handleClose={handleUserInfoClose} isOpen={userInfoOpen}>
          <Typography>It looks like you are missing some user information on your profile. Please click below to enter it now.</Typography>
          <StandardDialogActions>
            <Button variant="outlined" onClick={() => handleUserInfoClose()}>Close</Button>
            <Link to={'/user-profile/'}><Button variant="contained">Go To User Info</Button></Link>
          </StandardDialogActions>
        </StandardDialog>}
    </>
  );

  // Hold render until the feature flags resolve, so users never see a flash of the
  // wrong navigation shell. The flag cache is fetched once, so this only affects the
  // very first authenticated paint.
  if (!navFlagLoaded) return null;

  // Flag off or unavailable: the previous drawer side navigation, and no header bar.
  if (!showNavV2) {
    return (
      <Box sx={{ display: 'flex', minWidth: '320px' }}>
        <LegacySideNav />
        <Box
          component="main"
          sx={fullBleed
            ? { flexGrow: 1, minWidth: 0, bgcolor: 'background.default', overflow: 'hidden', display: 'flex', flexDirection: 'column' }
            : { flexGrow: 1, p: 3, bgcolor: 'background.default', minHeight: '100vh' }}
        >
          {pageBody}
        </Box>
      </Box>
    );
  }

  // Flag on: the new header bar + 90px icon rail shell.
  return (
    <SideNav>
      <Box
        component="main"
        sx={fullBleed
          ? { flexGrow: 1, minWidth: 0, bgcolor: 'background.default', overflow: 'hidden', display: 'flex', flexDirection: 'column' }
          : { flexGrow: 1, minWidth: 0, p: 3, bgcolor: 'background.default', overflowY: 'auto' }}
      >
        {pageBody}
      </Box>
    </SideNav>
  );
}

Layout.defaultProps = {
  includeHeader: true,
  includeDivider: true,
  fullBleed: false,
};

export default Layout;
